/**
 * MFA / TOTP for staff logins (tracker item 63). Mirrors backend
 * modules/mfa/mfa.types.ts and the login challenge in auth.types.ts.
 */

/** Route of the security page: enrollment (also the forced gate), disable, recovery codes. */
export const ACCOUNT_SECURITY_PATH = '/account/security';

/** Backend error code while the company requires MFA and the user has not enrolled. */
export const MFA_ENROLLMENT_REQUIRED_CODE = 'MFA_ENROLLMENT_REQUIRED';

/** Staff roles the company can require MFA for (backend MFA_ENFORCEABLE_ROLES). */
export type MfaEnforceableRole = 'ADMIN' | 'OFFICE_MANAGER' | 'TECHNICIAN';
export const MFA_ENFORCEABLE_ROLES: readonly MfaEnforceableRole[] = ['ADMIN', 'OFFICE_MANAGER', 'TECHNICIAN'];

/**
 * Thrown by login() when the password was right but MFA is on: there is no
 * session yet. Send `challengeToken` with a code (or a recovery code) to
 * verifyMfaLogin() within the challenge lifetime (5 minutes).
 */
export class MfaChallengeRequiredError extends Error {
  readonly challengeToken: string;
  readonly expiresAt: string;

  constructor(challengeToken: string, expiresAt: string) {
    super('Enter the code from your authenticator app');
    this.name = 'MfaChallengeRequiredError';
    this.challengeToken = challengeToken;
    this.expiresAt = expiresAt;
  }
}

/** POST /auth/login answer when MFA is on (backend LoginMfaChallengeResponse). */
export interface MfaLoginChallenge {
  mfaRequired: true;
  challengeToken: string;
  expiresAt: string;
}

/** A second factor: exactly one of the two. */
export interface MfaFactor {
  code?: string;
  recoveryCode?: string;
}

/** Re-authentication for turning MFA off, new recovery codes and admin resets. */
export interface MfaReauth extends MfaFactor {
  password: string;
}

/** GET /mfa/status */
export interface MfaStatus {
  /** The server can use MFA (APP_SECRET_KEY is set) */
  available: boolean;
  enabled: boolean;
  enabledAt: string | null;
  pendingEnrollment: boolean;
  /** The company requires MFA for the caller's role */
  required: boolean;
  recoveryCodesRemaining: number;
  lockedUntil: string | null;
}

/** POST /mfa/enrollment */
export interface MfaEnrollmentStart {
  /** For typing into the app by hand (shown during setup only) */
  secret: string;
  otpauthUri: string;
  /** data:image/svg+xml;base64,... */
  qrCodeDataUrl: string;
  expiresAt: string;
  issuer: string;
  account: string;
}

/** POST /mfa/enrollment/confirm */
export interface MfaEnrollmentConfirm {
  /** Shown once */
  recoveryCodes: string[];
  /** A fresh session token (older sessions stop working once MFA is on) */
  token: string;
}

/** GET / PUT /mfa/policy (ADMIN) */
export interface MfaPolicy {
  requiredRoles: MfaEnforceableRole[];
  enforceableRoles: MfaEnforceableRole[];
  available: boolean;
}

/** GET /mfa/users (ADMIN): users that have MFA on, started it or are locked. */
export interface UserMfaStatus {
  userId: string;
  enabled: boolean;
  enabledAt: string | null;
  lockedUntil: string | null;
}
